import type { Order, OrderLeg } from "~/v2/api/rest/account";
import type { OrdersWidgetBadgeColor } from "./OrdersWidget.types";
import {
  formatOrderLabel,
  formatOrderNotional,
  formatOrderPercentage,
  formatOrderPrice,
  formatOrderQuantity,
  formatOrderTimeInForce,
  formatOrderTimestamp,
  getOrderSideBadgeColor,
  getOrderStatusBadgeColor,
  ORDERS_UNAVAILABLE_VALUE,
} from "./OrdersWidget.utils";
import type { ReplacementHistory } from "./replacementChains";

export interface OrderDetailField {
  label: string;
  value: string;
  numeric?: boolean;
  monospace?: boolean;
  badgeColor?: OrdersWidgetBadgeColor;
}

export interface OrderLifecycleMilestone {
  label: string;
  timestamp: string;
  displayTimestamp: string;
}

export interface OrderLegSummary {
  id: string;
  title: string;
  fields: OrderDetailField[];
}

export interface OrderDetailViewModel {
  title: string;
  description: string;
  heroFields: OrderDetailField[];
  summaryFields: OrderDetailField[];
  priceFields: OrderDetailField[];
  replacementFields: OrderDetailField[];
  milestones: OrderLifecycleMilestone[];
  legs: OrderLegSummary[];
}

export type OrderVersionHistoryKind =
  | "primary"
  | "rejected-attempt"
  | "ambiguous";

export interface OrderVersionHistoryViewModel {
  key: string;
  kind: OrderVersionHistoryKind;
  title: string;
  tags: string[];
  changedFields: string;
  fields: OrderDetailField[];
}

export interface ReplacementHistoryViewModel {
  statusSequence: string;
  versions: OrderVersionHistoryViewModel[];
  issueMessages: string[];
  isComplete: boolean;
  isRootKnown: boolean;
}

const nonEmptyString = (value: unknown): string | undefined =>
  typeof value === "string" && value.trim() ? value.trim() : undefined;

const displayText = (value: unknown): string =>
  nonEmptyString(value) ?? ORDERS_UNAVAILABLE_VALUE;

const displayTimestamp = (value: string): string => {
  const formatted = formatOrderTimestamp(value);
  return formatted === ORDERS_UNAVAILABLE_VALUE ? value : formatted;
};

interface MilestoneCandidate {
  label: string;
  timestamp: unknown;
}

export const getOrderLifecycleMilestones = (
  order: Order,
): OrderLifecycleMilestone[] => {
  const status = nonEmptyString(order.status);
  const candidates: MilestoneCandidate[] = [
    {
      label: nonEmptyString(order.replaces) ? "Edit submitted" : "Submitted",
      timestamp: order.submitted_at,
    },
    ...(status === "filled"
      ? [{ label: "Filled", timestamp: order.filled_at }]
      : []),
    { label: "Canceled", timestamp: order.canceled_at },
    { label: "Replaced", timestamp: order.replaced_at },
    { label: "Rejected / failed", timestamp: order.failed_at },
    { label: "Expired", timestamp: order.expired_at },
  ];

  return candidates
    .flatMap((candidate, index) => {
      const timestamp = nonEmptyString(candidate.timestamp);
      return timestamp
        ? [
            {
              label: candidate.label,
              timestamp,
              displayTimestamp: displayTimestamp(timestamp),
              index,
            },
          ]
        : [];
    })
    .sort((first, second) => {
      const firstTime = Date.parse(first.timestamp);
      const secondTime = Date.parse(second.timestamp);
      const normalizedFirst = Number.isFinite(firstTime)
        ? firstTime
        : Number.POSITIVE_INFINITY;
      const normalizedSecond = Number.isFinite(secondTime)
        ? secondTime
        : Number.POSITIVE_INFINITY;
      return normalizedFirst - normalizedSecond || first.index - second.index;
    })
    .map(({ index: _index, ...milestone }) => milestone);
};

const optionalPriceField = (
  label: string,
  value: unknown,
  formatter: (candidate: unknown) => string = formatOrderPrice,
): OrderDetailField[] => {
  if (!nonEmptyString(value)) {
    return [];
  }
  return [{ label, value: formatter(value), numeric: true }];
};

const optionalTimestampField = (
  label: string,
  value: unknown,
): OrderDetailField[] => {
  const timestamp = nonEmptyString(value);
  return timestamp
    ? [{ label, value: displayTimestamp(timestamp), numeric: true }]
    : [];
};

const CHANGED_VERSION_FIELDS = [
  { label: "Quantity", read: (order: Order) => order.qty },
  { label: "Limit price", read: (order: Order) => order.limit_price },
  { label: "Stop price", read: (order: Order) => order.stop_price },
  { label: "Trail price", read: (order: Order) => order.trail_price },
  { label: "Trail percent", read: (order: Order) => order.trail_percent },
  { label: "Time in force", read: (order: Order) => order.time_in_force },
] as const;

const comparableValue = (value: unknown): string =>
  typeof value === "string" ? value.trim() : String(value ?? "");

const getChangedVersionFields = (
  order: Order,
  previousOrder?: Order,
): string => {
  if (!previousOrder) {
    return "Initial version";
  }
  const changed = CHANGED_VERSION_FIELDS.filter(
    ({ read }) =>
      comparableValue(read(order)) !== comparableValue(read(previousOrder)),
  ).map(({ label }) => label);
  return changed.length > 0
    ? `Changed: ${changed.join(", ")}`
    : "No editable field change is visible in this payload.";
};

const getVersionFields = (order: Order): OrderDetailField[] => [
  {
    label: "Order ID",
    value: displayText(order.id),
    monospace: true,
  },
  {
    label: "Status (raw)",
    value: displayText(order.status),
    badgeColor: getOrderStatusBadgeColor(order.status),
  },
  ...optionalTimestampField("Submitted", order.submitted_at),
  ...optionalTimestampField("Replaced", order.replaced_at),
  ...(normalizedStatus(order.status) === "filled"
    ? optionalTimestampField("Final filled", order.filled_at)
    : []),
  {
    label: "Quantity",
    value: formatOrderQuantity(order.qty),
    numeric: true,
  },
  ...optionalPriceField("Limit price", order.limit_price),
  ...optionalPriceField("Stop price", order.stop_price),
  ...optionalPriceField("Trail price", order.trail_price),
  ...optionalPriceField(
    "Trail percent",
    order.trail_percent,
    formatOrderPercentage,
  ),
  {
    label: "Time in force",
    value: formatOrderTimeInForce(order.time_in_force),
  },
];

const normalizedStatus = (status: unknown): string =>
  typeof status === "string" ? status.trim().toLowerCase() : "";

const mapHistoryVersion = ({
  clickedOrderID,
  headOrderID,
  isHeadKnown,
  isRootKnown,
  index,
  kind,
  order,
  previousOrder,
}: {
  clickedOrderID: string;
  headOrderID?: string;
  isHeadKnown: boolean;
  isRootKnown: boolean;
  index: number;
  kind: OrderVersionHistoryKind;
  order: Order;
  previousOrder?: Order;
}): OrderVersionHistoryViewModel => {
  const orderID = nonEmptyString(order.id);
  const tags = [
    ...(kind === "primary" && index === 0
      ? [isRootKnown ? "Original" : "Earliest loaded"]
      : []),
    ...(orderID && orderID === headOrderID
      ? [isHeadKnown ? "Current" : "Latest loaded"]
      : []),
    ...(orderID && orderID === clickedOrderID ? ["Clicked"] : []),
    ...(kind === "rejected-attempt" ? ["Rejected attempt"] : []),
    ...(kind === "ambiguous" ? ["Ambiguous branch"] : []),
  ];

  return {
    key: `${kind}-${orderID ?? index}`,
    kind,
    title:
      kind === "primary"
        ? index === 0
          ? isRootKnown
            ? "Original version"
            : "Earliest loaded version"
          : `Version ${index + 1}`
        : kind === "rejected-attempt"
          ? "Rejected replacement attempt"
          : "Related ambiguous version",
    tags,
    changedFields: getChangedVersionFields(order, previousOrder),
    fields: getVersionFields(order),
  };
};

export const mapReplacementHistory = (
  history: ReplacementHistory,
): ReplacementHistoryViewModel => {
  const allOrders = [
    ...history.versions,
    ...history.rejectedAttempts,
    ...history.ambiguousVersions,
  ];
  const orderByID = new Map(
    allOrders.flatMap((order) => {
      const id = nonEmptyString(order.id);
      return id ? [[id, order] as const] : [];
    }),
  );
  const headOrderID = nonEmptyString(history.headOrder.id);
  const primaryVersions = history.versions.map((order, index) =>
    mapHistoryVersion({
      clickedOrderID: history.clickedOrderID,
      headOrderID,
      isHeadKnown: history.isHeadKnown,
      isRootKnown: history.isRootKnown,
      index,
      kind: "primary",
      order,
      previousOrder: index > 0 ? history.versions[index - 1] : undefined,
    }),
  );
  const rejectedAttempts = history.rejectedAttempts.map((order, index) =>
    mapHistoryVersion({
      clickedOrderID: history.clickedOrderID,
      headOrderID,
      isHeadKnown: history.isHeadKnown,
      isRootKnown: history.isRootKnown,
      index,
      kind: "rejected-attempt",
      order,
      previousOrder: nonEmptyString(order.replaces)
        ? orderByID.get(nonEmptyString(order.replaces) ?? "")
        : undefined,
    }),
  );
  const ambiguousVersions = history.ambiguousVersions.map((order, index) =>
    mapHistoryVersion({
      clickedOrderID: history.clickedOrderID,
      headOrderID,
      isHeadKnown: history.isHeadKnown,
      isRootKnown: history.isRootKnown,
      index,
      kind: "ambiguous",
      order,
      previousOrder: nonEmptyString(order.replaces)
        ? orderByID.get(nonEmptyString(order.replaces) ?? "")
        : undefined,
    }),
  );
  const issueMessages = [
    ...new Set(history.issues.map(({ message }) => message)),
  ];

  return {
    statusSequence:
      history.versions
        .map((order) => formatOrderLabel(order.status))
        .join(" → ") || ORDERS_UNAVAILABLE_VALUE,
    versions: [...primaryVersions, ...rejectedAttempts, ...ambiguousVersions],
    issueMessages,
    isComplete: history.isComplete,
    isRootKnown: history.isRootKnown,
  };
};

const mapLeg = (leg: OrderLeg, index: number): OrderLegSummary => {
  const symbol = displayText(leg.symbol);
  const id = displayText(leg.id);
  return {
    id: `${id}-${index}`,
    title: symbol === ORDERS_UNAVAILABLE_VALUE ? `Leg ${index + 1}` : symbol,
    fields: [
      {
        label: "Side",
        value: formatOrderLabel(leg.side),
        badgeColor: getOrderSideBadgeColor(leg.side),
      },
      {
        label: "Quantity",
        value: formatOrderQuantity(leg.qty ?? leg.ratio_qty),
        numeric: true,
      },
      {
        label: "Filled quantity",
        value: formatOrderQuantity(leg.filled_qty),
        numeric: true,
      },
      {
        label: "Type",
        value: formatOrderLabel(leg.order_type || leg.type),
      },
      {
        label: "Current status",
        value: formatOrderLabel(leg.status),
        badgeColor: getOrderStatusBadgeColor(leg.status),
      },
    ],
  };
};

export const mapOrderDetail = (order: Order): OrderDetailViewModel => {
  const symbol = displayText(order.symbol);
  const orderID = displayText(order.id);
  const legs = Array.isArray(order.legs) ? order.legs : [];

  return {
    title:
      symbol === ORDERS_UNAVAILABLE_VALUE
        ? "Order details"
        : `${symbol} order details`,
    description:
      "Focused order details with bounded replacement-version history and paginated fills for this order.",
    heroFields: [
      {
        label: "Side",
        value: formatOrderLabel(order.side),
        badgeColor: getOrderSideBadgeColor(order.side),
      },
      {
        label: "Status",
        value: formatOrderLabel(order.status),
        badgeColor: getOrderStatusBadgeColor(order.status),
      },
      {
        label: "Type",
        value: formatOrderLabel(order.order_type || order.type),
        badgeColor: "gray",
      },
    ],
    summaryFields: [
      { label: "Symbol", value: symbol },
      { label: "Order ID", value: orderID, monospace: true },
      {
        label: "Client Order ID",
        value: displayText(order.client_order_id),
        monospace: true,
      },
      {
        label: "Quantity",
        value: formatOrderQuantity(order.qty),
        numeric: true,
      },
      {
        label: "Notional",
        value: formatOrderNotional(order.notional),
        numeric: true,
      },
      {
        label: "Filled quantity",
        value: formatOrderQuantity(order.filled_qty),
        numeric: true,
      },
      { label: "Class", value: formatOrderLabel(order.order_class) },
      {
        label: "Asset class",
        value: formatOrderLabel(order.asset_class),
      },
      {
        label: "Time in force",
        value: formatOrderTimeInForce(order.time_in_force),
      },
      {
        label: "Created",
        value: nonEmptyString(order.created_at)
          ? displayTimestamp(order.created_at)
          : ORDERS_UNAVAILABLE_VALUE,
        numeric: true,
      },
      {
        label: "Last updated",
        value: nonEmptyString(order.updated_at)
          ? displayTimestamp(order.updated_at)
          : ORDERS_UNAVAILABLE_VALUE,
        numeric: true,
      },
    ],
    priceFields: [
      ...optionalPriceField("Limit price", order.limit_price),
      ...optionalPriceField("Stop price", order.stop_price),
      ...optionalPriceField("Trail price", order.trail_price),
      ...optionalPriceField(
        "Trail percent",
        order.trail_percent,
        formatOrderPercentage,
      ),
      ...optionalPriceField("Filled average price", order.filled_avg_price),
    ],
    replacementFields: [
      ...(nonEmptyString(order.replaces)
        ? [
            {
              label: "Replaces order ID",
              value: displayText(order.replaces),
              monospace: true,
            },
          ]
        : []),
      ...(nonEmptyString(order.replaced_by)
        ? [
            {
              label: "Replaced by order ID",
              value: displayText(order.replaced_by),
              monospace: true,
            },
          ]
        : []),
    ],
    milestones: getOrderLifecycleMilestones(order),
    legs: legs.map(mapLeg),
  };
};
